import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { QrCode, Table, ChefHat } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8081';

export default function TestPage() {
  const [restaurantId, setRestaurantId] = useState(1);
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchTables = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/billing-pos/restaurants/${restaurantId}/areas/all-tables`
      );
      // Backend returns {tables: [...]} format
      const tableData = response.data.tables || [];
      // Map backend fields to frontend expected format
      const mappedTables = tableData.map(table => ({
        id: table.tableId,
        tableNumber: table.tableNumber,
        tableName: table.tableName,
        capacity: table.seatingCapacity,
        status: table.status
      }));
      setTables(mappedTables);
    } catch (error) {
      console.error('Error fetching tables:', error);
      setTables([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTables();
  }, [restaurantId]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 p-8">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-3xl shadow-2xl p-8 mb-8 border-t-4 border-orange-500">
          <div className="flex items-center gap-5 mb-8">
            <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl flex items-center justify-center shadow-lg">
              <ChefHat className="text-white" size={40} strokeWidth={2.5} />
            </div>
            <div>
              <h1 className="text-4xl font-extrabold text-gray-900 mb-1">QR Menu Testing</h1>
              <p className="text-gray-600 text-lg">Test the QR menu system locally without scanning</p>
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-bold text-gray-700 mb-3 uppercase tracking-wide">
              Restaurant ID
            </label>
            <div className="flex gap-4">
              <input
                type="number"
                value={restaurantId}
                onChange={(e) => setRestaurantId(parseInt(e.target.value))}
                className="flex-1 px-5 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all text-lg font-semibold"
                placeholder="Enter Restaurant ID"
              />
              <button
                onClick={fetchTables}
                className="px-8 py-3 bg-gradient-to-r from-orange-600 to-red-600 text-white font-bold rounded-xl hover:shadow-xl hover:scale-105 active:scale-95 transition-all text-base"
              >
                Load Tables
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16">
            <div className="inline-block w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-600 mt-4 font-semibold text-lg">Loading tables...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {tables.map((table) => (
              <Link
                key={table.id}
                to={`/menu?r=${restaurantId}&t=${table.id}`}
                className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 p-6 border-2 border-transparent hover:border-orange-500 hover:scale-105 active:scale-95"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-orange-100 to-red-100 group-hover:from-orange-500 group-hover:to-red-600 rounded-xl flex items-center justify-center transition-all shadow-md">
                    <Table className="text-orange-600 group-hover:text-white transition-colors" size={28} strokeWidth={2.5} />
                  </div>
                  <div>
                    <h3 className="font-bold text-xl text-gray-900 group-hover:text-orange-600 transition-colors">
                      {table.tableName || `Table ${table.tableNumber}`}
                    </h3>
                    <p className="text-sm text-gray-600 font-medium">
                      Capacity: {table.capacity || 4} guests
                    </p>
                  </div>
                </div>
                
                <div className="pt-4 border-t-2 border-gray-100">
                  <div className="flex items-center justify-between">
                    <span className={`px-4 py-1.5 rounded-full text-xs font-bold shadow-sm ${
                      table.status === 'AVAILABLE' 
                        ? 'bg-green-100 text-green-700 border border-green-300' 
                        : 'bg-orange-100 text-orange-700 border border-orange-300'
                    }`}>
                      {table.status || 'AVAILABLE'}
                    </span>
                    <QrCode className="text-gray-400 group-hover:text-orange-500 transition-colors" size={24} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {!loading && tables.length === 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-16 text-center border-2 border-dashed border-gray-300">
            <Table className="w-20 h-20 text-gray-300 mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="text-2xl font-bold text-gray-800 mb-2">No Tables Found</h3>
            <p className="text-gray-600 text-lg">
              Please check the restaurant ID or create tables first
            </p>
          </div>
        )}

        <div className="mt-8 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-2xl p-8 shadow-lg">
          <h3 className="font-bold text-blue-900 mb-4 flex items-center gap-3 text-xl">
            <span className="text-3xl">💡</span> Testing Instructions
          </h3>
          <ul className="space-y-3 text-sm text-blue-800">
            <li className="flex items-start gap-2">
              <span className="font-bold text-blue-600 mt-0.5">→</span>
              <span>Enter your restaurant ID and click <strong>"Load Tables"</strong></span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold text-blue-600 mt-0.5">→</span>
              <span>Click on any table card to open the menu for that table</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold text-blue-600 mt-0.5">→</span>
              <span>The URL will be: <code className="bg-blue-100 px-3 py-1 rounded-lg font-mono text-xs border border-blue-300">/menu?r=1&t=5</code></span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold text-blue-600 mt-0.5">→</span>
              <span>This simulates scanning a QR code for that specific table</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold text-blue-600 mt-0.5">→</span>
              <span>You can test adding items to cart and placing orders</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
